import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  Add,
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../common/StatusChip";
import { RowActions } from "../common/RowActions";
import { getYearToDateRange } from "../../utils/helpers";
import { useGetMallsAQuery, useLazyGetMallsAQuery } from "../../services/mall.query";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";
import { useTableState } from "../../hooks/useTableState";
import { Notify } from "../../layouts/notify";

export default function MallsPage() {
  const navigate = useNavigate();
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getYearToDateRange();

  const defaultFilters = useMemo(
    () => ({
      searchTerm: "",
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    }),
    [defaultStartDate, defaultEndDate]
  );

  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting: updateTableSorting,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
    defaultSortBy: "movie_reports_count",
    defaultSortOrder: "desc",
  });

  const [draftSearch, setDraftSearch] = useState(filters.searchTerm);
  const [draftStartDate, setDraftStartDate] = useState(filters.startDate);
  const [draftEndDate, setDraftEndDate] = useState(filters.endDate);

  useEffect(() => {
    setDraftSearch(filters.searchTerm);
    setDraftStartDate(filters.startDate);
    setDraftEndDate(filters.endDate);
  }, [filters]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const [triggerFetchAllMalls] = useLazyGetMallsAQuery();

  const { data, isLoading, isFetching } = useGetMallsAQuery({
    page,
    perPage: pageSize,
    searchTerm: filters.searchTerm,
    startDate: filters.startDate || null,
    endDate: filters.endDate || null,
    sortBy,
    sortDir: sortOrder as "asc" | "desc",
  });

  const malls = data?.list ?? [];
  const totalMallCount = data?.count ?? 0;

  const handleApplyFilters = () => {
    setFilters({
      searchTerm: draftSearch.trim(),
      startDate: draftStartDate,
      endDate: draftEndDate,
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching malls and independent theatres...");
    try {
      const result = await triggerFetchAllMalls({
        page: 1,
        perPage: 999999,
        searchTerm: filters.searchTerm,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
        sortBy,
        sortDir: sortOrder as "asc" | "desc",
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Preparing PDF document for all ${allRows.length.toLocaleString()} malls...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Registered Malls & Independent Theatres", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Malls: ${allRows.length}`,
        14,
        22
      );

      const tableColumn = [
        "#",
        "Code Pass",
        "Mall / Theatre Group Name",
        "Address",
        "Movie Reports Count",
        "App Displayed",
      ];

      const tableRows = allRows.map((item: any, idx: number) => {
        const address = [item.street_address, item.barangay_address, item.city_address, item.province_address]
          .filter(Boolean)
          .join(", ") || "—";
        return [
          idx + 1,
          item.code_pass || "—",
          item.name || "—",
          address,
          String(item.report_count ?? 0),
          item.is_app_displayed ? "Visible" : "Hidden",
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Malls_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length} malls to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching malls and independent theatres...");
    try {
      const result = await triggerFetchAllMalls({
        page: 1,
        perPage: 999999,
        searchTerm: filters.searchTerm,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
        sortBy,
        sortDir: sortOrder as "asc" | "desc",
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Generating Excel spreadsheet for all ${allRows.length.toLocaleString()} malls...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const rows = allRows.map((item: any, idx: number) => {
        const address = [item.street_address, item.barangay_address, item.city_address, item.province_address]
          .filter(Boolean)
          .join(", ") || "—";
        return {
          "No.": idx + 1,
          "Code Pass": item.code_pass || "—",
          "Mall Name": item.name || "—",
          "Street Address": item.street_address || "—",
          "Barangay": item.barangay_address || "—",
          "City": item.city_address || "—",
          "Province": item.province_address || "—",
          "Movie Reports Count": item.report_count ?? 0,
          "App Displayed": item.is_app_displayed ? "Visible" : "Hidden",
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Malls Directory");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 30 },
        { wch: 24 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 16 },
      ];

      XLSX.writeFile(wb, `MTRCB_Malls_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${allRows.length} malls to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const number = (page - 1) * pageSize + row.index + 1;
          return <Typography>{number}</Typography>;
        },
      },
      {
        id: "code_pass",
        header: "Code Pass",
        size: 120,
        Cell: ({ row }: any) => (
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#0072BC" }}>
            {row.original.code_pass || "—"}
          </Typography>
        ),
      },
      {
        id: "logo_url",
        header: "Logo",
        size: 90,
        Cell: ({ row }: any) =>
          row.original.logo_url ? (
            <img
              src={row.original.logo_url}
              alt={row.original.name}
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 4,
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              No logo
            </Typography>
          ),
      },
      {
        id: "name",
        header: "Mall Name",
        size: 260,
        Cell: ({ row }: any) => (
          <Typography fontWeight={600} color="#1E293B">
            {row.original.name}
          </Typography>
        ),
      },
      {
        id: "address",
        header: "Address",
        size: 320,
        Cell: ({ row }: any) => {
          const m = row.original;
          const parts = [m.street_address, m.barangay_address, m.city_address, m.province_address].filter(Boolean);
          return (
            <Typography sx={{ fontSize: 13, color: "#475569" }}>
              {parts.join(", ") || "—"}
            </Typography>
          );
        },
      },
      {
        id: "report_count",
        header: "Movie Reports",
        size: 150,
        Cell: ({ row }: any) => (
          <Typography fontWeight={700} sx={{ color: "#0F172A" }}>
            {Number(row.original.report_count ?? 0).toLocaleString()}
          </Typography>
        ),
      },
      {
        id: "is_app_displayed",
        header: "App Display",
        size: 130,
        Cell: ({ row }: any) => {
          const displayed = Boolean(row.original.is_app_displayed);
          return (
            <StatusChip
              label={displayed ? "Visible" : "Hidden"}
              tone={displayed ? "success" : "neutral"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 130,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "View",
                onClick: () => navigate(`/movie-house/malls/${row.original.id}`),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize, navigate]
  );

  return (
    <DefaultPage 
      title="Malls / Independent Theatres" 
      subTitle="Manage and review registered malls and independent theatres."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered malls. Set the filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={(e) => {
                setPage(1);
                updateTableSorting(e.target.value, sortOrder);
              }}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="movie_reports_count">Movie Reports</MenuItem>
              <MenuItem value="name">Mall Name</MenuItem>
              <MenuItem value="code_pass">Code Pass</MenuItem>
            </TextField>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPage(1);
                updateTableSorting(sortBy, sortOrder === "asc" ? "desc" : "asc");
              }}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "4px",
                borderColor: "#CBD5E1",
                color: "#334155",
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
              }}
            >
              {sortOrder === "asc" ? "Asc ↑" : "Desc ↓"}
            </Button>

            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              size="small"
              type="date"
              label="End Date"
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              size="small"
              label="Search name or code pass"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
              sx={{
                minWidth: 260,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleApplyFilters}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={() => navigate("new")}
                sx={{
                  backgroundColor: "#00934C",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#007a3f",
                    boxShadow: "none",
                  },
                }}
              >
                Add Mall
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalMallCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={malls}
        total={totalMallCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: { id: any }) => String(row.id)}
        isLoading={isLoading || isFetching}
        onRowClick={(row: any) => navigate(`/movie-house/malls/${row.original.id}`)}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />
    </DefaultPage>
  );
}